import {
  Badge,
  Button,
  Callout,
  Card,
  CellSub,
  Checkbox,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  NativeSelect,
} from '@provenance/design';
import { MasterDataListType, type MasterDataItem } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileUp,
  Info,
  ScrollText,
  Upload,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { auDate, auDateTime, relative, auCalendarDate} from '../lib/format';

/**
 * Certificate modification requests (spec §7.4).
 *
 * The distinction this screen exists to protect: a modification is **not** a revocation.
 * An exporter with a certificate whose stamp sits over a box has a presentation problem,
 * not a validity problem. Revoking would flip public verification to REVOKED for a
 * document already at a border — wildly disproportionate.
 *
 * So the certificate number never changes, the certificate stays valid throughout, and
 * only the downloadable file is replaced. The screen says that in as many words, because
 * an operator's instinct on hearing "this certificate is wrong" is to revoke it.
 */

interface ModificationRow {
  id: string;
  documentId: string;
  certificateNo: string;
  requestTypes: string[];
  comments: string;
  status: string;
  requestedAt: string;
  processedAt: string | null;
  hasCorrectedFile: boolean;
  exporterName: string;
  documentTypeCode: string;
  documentTypeName: string;
  certificateDate: string | null;
}

const STATUS_TONE: Record<string, 'warn' | 'ok' | 'neutral'> = {
  PENDING: 'warn',
  PROCESSED: 'ok',
  CANCELLED: 'neutral',
};

export function Modifications() {
  const [selected, setSelected] = useState<ModificationRow | null>(null);

  const query = useQuery({
    queryKey: ['modifications'],
    queryFn: () => api.get<ModificationRow[]>('/api/console/modifications'),
  });

  const rows = query.data ?? [];
  const pending = rows.filter((r) => r.status === 'PENDING');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Modifications"
        lede="Where something on an already-issued certificate is printed wrong — a stamp over a box, a date in the wrong place. The certificate number does not change and the certificate stays valid."
      />

      <Callout tone="info" icon={<Info className="size-4" />} title="This is not revocation">
        Processing a correction replaces the file the exporter downloads, under the same
        certificate number. Public verification is unaffected. If the certificate's{' '}
        <em>content</em> is wrong — the wrong consignee, the wrong goods — that is a revocation
        and a reissue, from the certificate's own screen.
      </Callout>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          tone="good"
          icon={<CheckCircle2 className="size-5" />}
          title="No corrections outstanding"
          description="When an exporter reports that something on an issued certificate is printed wrong, it appears here. You can also raise one from a certificate's own screen."
        />
      ) : (
        <TableShell footer={<span>{pending.length} awaiting action · {rows.length} in total</span>}>
          <Table className="min-w-[840px]">
            <Thead>
              <Tr>
                <Th>Certificate</Th>
                <Th>Exporter</Th>
                <Th>What needs correcting</Th>
                <Th>Requested</Th>
                <Th>Status</Th>
                <Th className="w-28" />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <span className="pv-mono font-semibold">{row.certificateNo}</span>
                    <CellSub>{row.documentTypeName}</CellSub>
                  </Td>
                  <Td>
                    {row.exporterName}
                    {row.certificateDate && <CellSub>issued {auCalendarDate(row.certificateDate)}</CellSub>}
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {row.requestTypes.map((type) => (
                        <CodeChip key={type}>{type}</CodeChip>
                      ))}
                    </span>
                    <CellSub className="max-w-[34ch] truncate">{row.comments}</CellSub>
                  </Td>
                  <Td>
                    {auDate(row.requestedAt)}
                    <CellSub>{relative(row.requestedAt)}</CellSub>
                  </Td>
                  <Td>
                    <Badge tone={STATUS_TONE[row.status] ?? 'neutral'} size="sm" dot={row.status === 'PENDING'}>
                      {row.status === 'PENDING' ? 'Awaiting action' : row.status === 'PROCESSED' ? 'Corrected' : 'Withdrawn'}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="flex gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(row)}>
                        {row.status === 'PENDING' ? 'Action' : 'View'}
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/documents/${row.documentId}`}>Certificate</Link>
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}

      {selected && (
        <ProcessDialog
          request={selected}
          open
          onOpenChange={(next) => !next && setSelected(null)}
        />
      )}
    </div>
  );
}

interface ModificationDetail {
  id: string;
  documentId: string;
  certificateNo: string;
  requestTypes: Array<{ code: string; label: string }>;
  comments: string;
  status: string;
  requestedAt: string;
  processedAt: string | null;
  exporterName: string;
  senderReference: string;
  documentTypeName: string;
  certificateDate: string | null;
  issuedFileUrl: string | null;
  correctedFileUrl: string | null;
}

function ProcessDialog({
  request,
  open,
  onOpenChange,
}: {
  request: ModificationRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const detail = useQuery({
    queryKey: ['modification', request.id],
    queryFn: () => api.get<ModificationDetail>(`/api/console/modifications/${request.id}`),
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['modifications'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['document', request.documentId] });
    onOpenChange(false);
  };

  const process = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file chosen');
      return api.post(`/api/console/modifications/${request.id}/process`, {
        filename: file.name,
        contentBase64: await toBase64(file),
      });
    },
    onSuccess: () => {
      toast.toast({
        tone: 'success',
        title: `Certificate ${request.certificateNo} corrected`,
        description: 'The exporter has been emailed. The certificate number is unchanged.',
      });
      done();
    },
    onError: (error) =>
      toast.error(
        'Could not upload the corrected certificate',
        error instanceof ApiError ? error.message : 'Check the file is a valid PDF.',
      ),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) =>
      api.post(`/api/console/modifications/${request.id}/cancel`, { reason }),
    onSuccess: () => {
      toast.success('Request withdrawn');
      done();
    },
  });

  const pending = request.status === 'PENDING';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={`Certificate ${request.certificateNo}`}
          title={pending ? 'Correct this certificate' : 'Correction request'}
          description={
            pending
              ? 'Upload the corrected certificate. The number does not change and the certificate stays valid.'
              : 'This request has already been actioned.'
          }
        />
        <DialogBody className="flex flex-col gap-4">
          {detail.isLoading ? (
            <Skeleton className="h-40" />
          ) : (
            <>
              <Card className="!bg-[var(--pv-card-alt)]">
                <dl className="m-0">
                  <KeyValue label="Certificate" mono>
                    {request.certificateNo}
                  </KeyValue>
                  <KeyValue label="Exporter">{request.exporterName}</KeyValue>
                  <KeyValue label="Document type">{request.documentTypeName}</KeyValue>
                  <KeyValue label="Requested">{auDateTime(request.requestedAt)}</KeyValue>
                </dl>
              </Card>

              <div>
                <p className="pv-eyebrow text-[var(--pv-faint)]">What needs correcting</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(detail.data?.requestTypes ?? []).map((type) => (
                    <Badge key={type.code} tone="clay" size="sm">
                      {type.label}
                    </Badge>
                  ))}
                </div>
                <p className="mt-3 border-l-2 border-[var(--pv-line-strong)] pl-3 [font-size:13.5px] leading-relaxed text-[var(--pv-ink-soft)]">
                  {request.comments}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {detail.data?.issuedFileUrl && (
                  <Button variant="secondary" size="sm" asChild leadingIcon={<Download className="size-3.5" />}>
                    <a href={detail.data.issuedFileUrl} target="_blank" rel="noreferrer">
                      Download as issued
                    </a>
                  </Button>
                )}
                {detail.data?.correctedFileUrl && (
                  <Button variant="secondary" size="sm" asChild leadingIcon={<Download className="size-3.5" />}>
                    <a href={detail.data.correctedFileUrl} target="_blank" rel="noreferrer">
                      Download corrected
                    </a>
                  </Button>
                )}
              </div>

              {pending && (
                <div className="border-t border-[var(--pv-line)] pt-4">
                  <Field
                    label="The corrected certificate"
                    required
                    help="A PDF. It replaces what the exporter downloads; the original stays on file."
                  >
                    <div>
                      <input
                        ref={fileInput}
                        type="file"
                        accept="application/pdf"
                        className="sr-only"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        aria-label="Choose the corrected certificate PDF"
                      />
                      {file ? (
                        <div className="flex items-center gap-2.5 rounded-[var(--pv-radius-sm)] border border-[var(--pv-brand)] bg-[var(--pv-brand-soft)] px-3 py-2.5">
                          <FileUp aria-hidden className="size-4 shrink-0" />
                          <span className="min-w-0 flex-1 truncate [font-size:13px] font-medium">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => setFile(null)}
                            aria-label="Remove the file"
                            className="rounded-[var(--pv-radius-xs)] p-1 text-[var(--pv-muted)] hover:text-[var(--pv-stop)]"
                          >
                            <X aria-hidden className="size-3.5" />
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          leadingIcon={<Upload className="size-3.5" />}
                          onClick={() => fileInput.current?.click()}
                        >
                          Choose the corrected PDF
                        </Button>
                      )}
                    </div>
                  </Field>

                  <div className="mt-3">
                    <Checkbox
                      checked={confirmed}
                      onCheckedChange={(v) => setConfirmed(v === true)}
                      label="This corrects only how the certificate is presented, not what it says."
                      description="If the content is wrong — the wrong consignee, the wrong goods — revoke and reissue instead."
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter
          note={pending ? `Certificate ${request.certificateNo} remains valid throughout.` : undefined}
        >
          {pending ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt('Why is this request being withdrawn?');
                  if (reason?.trim()) cancel.mutate(reason.trim());
                }}
                loading={cancel.isPending}
              >
                Withdraw
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                variant="primary"
                disabled={!file || !confirmed}
                loading={process.isPending}
                onClick={() => process.mutate()}
              >
                Upload the correction
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Raised from a certificate's own screen, usually after the exporter phones.
 * The same shape an exporter portal or an integrator endpoint would post.
 */
export function RaiseModificationDialog({
  documentId,
  certificateNo,
  open,
  onOpenChange,
}: {
  documentId: string;
  certificateNo: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [types, setTypes] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  // Who wanted this, as distinct from the operator filling the form in. An upload or
  // email exporter has no route to a correction through the product, so they telephone —
  // and until now nothing recorded that they had asked at all.
  const [askedBy, setAskedBy] = useState<'CHAMBER' | 'EXPORTER'>('CHAMBER');
  const [askedVia, setAskedVia] = useState<'PHONE' | 'EMAIL' | 'IN_PERSON' | 'OTHER'>('PHONE');

  const options = useQuery({
    queryKey: ['master-data', MasterDataListType.MODIFICATION_REQUEST_TYPE],
    queryFn: () =>
      api.get<MasterDataItem[]>(`/api/console/master-data/${MasterDataListType.MODIFICATION_REQUEST_TYPE}`),
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/console/modifications', {
        documentId,
        requestTypes: types,
        comments: comments.trim(),
        askedBy,
        askedVia: askedBy === 'EXPORTER' ? askedVia : null,
      }),
    onSuccess: () => {
      toast.success('Correction request raised', 'It is in the modifications queue.');
      void queryClient.invalidateQueries({ queryKey: ['modifications'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
      setTypes([]);
      setComments('');
      setAskedBy('CHAMBER');
    },
    onError: (error) =>
      toast.error('Could not raise the request', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader
          eyebrow={`Certificate ${certificateNo}`}
          title="Raise a correction"
          description="For something printed wrong on an issued certificate. The number does not change and it stays valid."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="warn" icon={<AlertTriangle className="size-4" />}>
            If the certificate's <strong>content</strong> is wrong rather than its presentation,
            revoke and reissue instead — a correction does not change what the certificate says.
          </Callout>

          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
              What needs correcting? <span className="font-bold text-[var(--pv-stop)]">*</span>
            </legend>
            <div className="flex flex-col gap-2">
              {(options.data ?? []).map((option) => (
                <Checkbox
                  key={option.id}
                  checked={types.includes(option.code)}
                  onCheckedChange={(checked) =>
                    setTypes((prev) =>
                      checked === true ? [...prev, option.code] : prev.filter((c) => c !== option.code),
                    )
                  }
                  label={option.label}
                />
              ))}
            </div>
          </fieldset>

          <Field
            label="Who asked for this?"
            required
            help="The exporter is emailed when it is processed — to an address you already hold, never one from the request."
          >
            <NativeSelect value={askedBy} onChange={(e) => setAskedBy(e.target.value as 'CHAMBER' | 'EXPORTER')}>
              <option value="CHAMBER">We found it ourselves</option>
              <option value="EXPORTER">The exporter asked</option>
            </NativeSelect>
          </Field>

          {askedBy === 'EXPORTER' && (
            <Field
              label="How did they ask?"
              required
              help="The only record that they asked at all, and the only way to know how often this happens."
            >
              <NativeSelect value={askedVia} onChange={(e) => setAskedVia(e.target.value as never)}>
                <option value="PHONE">By telephone</option>
                <option value="EMAIL">By email</option>
                <option value="IN_PERSON">In person</option>
                <option value="OTHER">Some other way</option>
              </NativeSelect>
            </Field>
          )}

          <Field label="What is wrong" required help="Recorded on the request and in the audit trail.">
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder="The chamber stamp is sitting over box 9 and obscures the invoice number."
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={types.length === 0 || comments.trim().length === 0}
            loading={create.isPending}
            onClick={() => create.mutate()}
            leadingIcon={<ScrollText className="size-3.5" />}
          >
            Raise the request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
